import { BCMSCurrency, BCMSDate, BCMSRecord, useGlobalController, useWorkflowStep } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "compid/custom-ui/CustomSelect";
import { DatePickerView } from "compid/ui/DatePicker";
import UIAmountInput from "compid/ui/UIAmountInput";
import UILimitedCharacterInput from "compid/ui/UILimitedCharacterInput";
import UIToggle from "compid/ui/UIToggle";
import { getDefaultAutopay, getEditAutopay, getWorkflowSteps } from "compid/workflows/AddAutoPay";
import { Account, Autopay, Bill, Biller, Payment } from "model";
import React from "react";
import { SVGAsset } from "res";
import NonEBillForm from "./components/NonEBillForm";
import "./styles.css";

/**
 * @function AutopayFormView
 * @param {String} className - Allows us to set a custom class name for the component
 * @return {React.Component}
 * @exported true
 * @description The autopay form
 */

export default function AutopayFormView(props){
  const className = props.className ?? "";

  const controller = useGlobalController();
    // Get the workflow step and initalize model classes
    const workflowStep = useWorkflowStep(props.isEditing ? getEditAutopay : getDefaultAutopay, getWorkflowSteps);
    BCMSRecord.globalControllerFor([ Autopay, Bill, Biller, Payment ], controller);
    
    const biller = workflowStep.read("biller");
    const autopay = workflowStep.read("autopay");
    const payment = workflowStep.read("payment");

    const isVirtualCard = biller.paymentType === "virtualCard";
    const { branding } = workflowStep.model ?? {};
    const billerType = biller.paymentType;

    const account = `${autopay.payFrom.name} ${autopay.payFrom.number}`;
    const email = workflowStep.model.emails[0].emailName;

    const hasAutopays = Autopay.initAll().filter(item => item.billerID === biller.id, []).length > 0;
    let showShortForm = !hasAutopays && biller.eBillStatus === "Active";

    // Short form condition added for "edit autopay" support.
    if(autopay.alwaysPay === "Fixed amount" || autopay.alwaysPay === "Amount due") showShortForm = true;

    const autopayDeliverBy = autopay.deliverBy instanceof BCMSDate ? autopay.deliverBy : autopay.deliverBy.toLowerCase();
    // Define update functions for properties in the autopay object
    const updateAmount = value => workflowStep.update(["autopay", "alwaysPay"], BCMSCurrency.init(value.replace("$", "")));
    const updateProp = prop => value => workflowStep.update(["autopay", prop], value);
    const updateCurrency = prop => value => workflowStep.update(["autopay", prop], value.replace("$", ""));
    const updateAccount = value => {
        workflowStep.update([ "autopay", "payFrom" ], value);
        workflowStep.update([ "payment", "from" ], value);
    };

    const firstDeliveryConditionalDisable = date => autopay.finalDeliveryBy.isPast() ? false : date.isAfter(autopay.finalDeliveryBy);
    const withdrawDate = Payment.getWithdrawOnDate(autopay.deliverBy, biller.paymentType, branding);
    const firstDeliveryDateHelperText1 = "Payments that fall on a weekend or holiday will be changed to previous business day.";
    const hasRealTimeGoodFunds = Payment.hasRealTimeGoodFunds(biller.paymentType, branding);
    const firstDeliveryDateHelperText2 = `First payment will be withdrawn on ${ withdrawDate.format("M d") }`;
    const availableBalanceText = `Available balance: ${ payment.from.balance.format() }`;
    let autopayAmount = BCMSCurrency.init(0);
    const autopayAlwaysPay = autopay.alwaysPay;

    // "autopay.alwaysPay" type varies when toggling the "Always pay" dropdown.
    if(autopayAlwaysPay instanceof BCMSCurrency) {
        autopayAmount = autopayAlwaysPay;
    } else if(autopayAlwaysPay === "Amount due") {
        autopayAmount = autopay.maximum;
    } else if(autopayAlwaysPay === "Fixed amount") {
        autopayAmount = autopay.fixed;
    }

    const fundsMessage1 = `The funds of ${ autopayAmount.format() } will be withdrawn from your account ${ autopay.payFrom.number } immediately for the first payment.`;
    let fundsMessage2 = `The funds of ${ autopayAmount.format() } will be withdrawn from your account ${ autopay.payFrom.number } `;
    const fundsMessage3 = `If funds are not available on the withdraw date, the payment will fail.`;

    // "fundsMessage2" changes based on biller type.
    if(billerType === "electronic") {
        fundsMessage2 = `${ fundsMessage2 } on the business day prior to the delivery date for the future payments.`;
    } else {
        fundsMessage2 = `${ fundsMessage2 } five business days prior to the delivery date for the future payments.`;
    }

    const customSelectItems = Account.initAll().map((account) => (<CustomSelectItem key={ account.id } value={ account.id }>{ account.name + " " + account.number }</CustomSelectItem>), []);

  return(
    <div className="autopay-form-view">
        <div className="flex-container flex-vertical form-group">
          {
              !showShortForm && // This section shows if the biller is not an Ebill
              <div className="form-row flex-height">
                  <DatePickerView dateLabel="First delivery date" setDate={autopay.deliverBy} onChange={updateProp("deliverBy")}
                      billerType={biller.paymentType} conditionalDisable={firstDeliveryConditionalDisable} dataPoint="DatePicker_First_Delivery_Date">
                      { hasRealTimeGoodFunds &&
                      <div className="flex-container flex-horizontal align-center date-helper-text">
                          <span className="sub-text">{ firstDeliveryDateHelperText2 }</span>
                      </div>
                      }
                      <div className="flex-container flex-horizontal align-center date-helper-text">
                          <span className="sub-text">{ firstDeliveryDateHelperText1 }</span>
                      </div>
                  </DatePickerView>
              </div>
          }

          {
              showShortForm && // This section shows if the biller is an Ebill
              <div className="form-row flex-height custom-select-form-row">
                  <CustomSelect label="Deliver by" helpText=" " value={autopay.deliverBy} onChange={updateProp("deliverBy")} dataPoint="Add_Autopay_Deliver_By_Select">
                      <CustomSelectItem>Due Date</CustomSelectItem>
                      <CustomSelectItem>Earliest delivery date</CustomSelectItem>
                      <CustomSelectItem>1 day before due date</CustomSelectItem>
                      <CustomSelectItem>2 days before due date</CustomSelectItem>
                      <CustomSelectItem>3 days before due date</CustomSelectItem>
                      <CustomSelectItem>4 days before due date</CustomSelectItem>
                      <CustomSelectItem>5 days before due date</CustomSelectItem>
                  </CustomSelect>

                  <div className="flex-container flex-horizontal align-center">
                      <span className="sub-text">Payments that fall on a weekend or holiday will be changed to previous business day.</span>
                  </div>
              </div>
          }

          <div className="pay-from-row form-row flex-height">
              { /**<UITextInput placeHolder="Pay from" value={[account]} dataPoint="Add_Autopay_Pay_From">
              { hasRealTimeGoodFunds &&
                <div className="flex-container flex-horizontal align-center">
                    <span className="sub-text">{ availableBalanceText }</span>
                </div>}
              </UITextInput> */}

              <CustomSelect label="Pay from" helpText={""} value={ payment.from.id } onChange={ updateAccount } dataPoint="Add_Autopay_Pay_From">
                { customSelectItems }
              </CustomSelect>

              { hasRealTimeGoodFunds &&
                <div className="flex-container flex-horizontal align-center">
                    <span className="sub-text">{ availableBalanceText }</span>
                </div> }
          </div>

          {
              !showShortForm && // This section shows if the biller is not an Ebill
              <div className="always-pay-input-row">
                  <UIAmountInput placeHolder="Always pay" value={ autopay.alwaysPay instanceof BCMSCurrency ? autopay.alwaysPay.format() : "" } onChange={updateAmount} dataPoint="Add_Autopay_Always_Pay" />
              </div>
          }

          {
              showShortForm && // This section shows if the biller is an Ebill
              <>
                  <div className="always-pay-row custom-select-form-row form-row">
                      <CustomSelect label="Always pay" helpText=" " value={autopay.alwaysPay} onChange={updateProp("alwaysPay")} dataPoint="Add_Autopay_Always_Pay_Select">
                          <CustomSelectItem dataPoint="Add_Autopay_Amount_Due_Item">Amount due</CustomSelectItem>
                          <CustomSelectItem dataPoint="Add_Autopay_Fixed_Amount_Item">Fixed amount</CustomSelectItem>
                      </CustomSelect>

                      <div className="flex-container flex-horizontal align-center">
                          <span className="blue-text sub-text payment-amount-text">About payment amounts</span>
                          <SVGAsset id="UpsellIcon" className="blue-icon icon-right" />
                      </div>
                  </div>

                  {/* Maximum payment only shows if Amount Due is selected from the Always pay Dropdown */}
                  {
                      autopay.alwaysPay === "Amount due" &&
                      <div className="maximum-payment-row">
                          <UIAmountInput placeHolder={autopayDeliverBy === "earliest delivery date" ? "Maximum payment" : "Maximum payment (optional)"}
                              value={autopay.maximum.format()} onChange={updateCurrency("maximum")} dataPoint="Add_Autopay_Maximum_Payment_Input">
                              <span className="sub-text">Set a limit on how much we will pay automatically.</span>
                          </UIAmountInput>
                      </div>
                  }

                  {/* Pay fixed amount only shows if Fixed amount is selected from the Always pay Dropdown */}
                  {
                      autopay.alwaysPay === "Fixed amount" &&
                      <div className="pay-fixed-amount-row">
                          <UIAmountInput placeHolder="Pay fixed amount" value={autopay.fixed.format()} onChange={updateCurrency("fixed")} />
                      </div>
                  }
              </>
          }

          {
              !showShortForm && // This section shows if the biller is not an Ebill
              <>
                <NonEBillForm workflowStep={ workflowStep } />

                {biller.paymentType === "paper" && <UILimitedCharacterInput placeHolder="Memo" value={autopay.memo} onChange={updateProp("memo")} dataPoint="Add_Autopay_Memo" />}
              </>
          }

          <div className="email-notifications-container">
              <p>Send email notifications to <b data-point="Add_Autopay_Email">{ email }</b> when the payment:</p>
              <label className="flex-container flex-horizontal align-center space-between is-scheduled clickable">
                  <p data-point="Add_Autopay_Is_Scheduled">Is scheduled</p>
                  <UIToggle text="" checked={ autopay.notificationScheduled } onChange={ updateProp("notificationScheduled") } dataPoint="Add_Autopay_Is_Scheduled_Toggle" />
              </label>
              <label className="flex-container flex-horizontal align-center space-between has-been-sent clickable">
                  <p data-point="Add_Autopay_Has_Been_Sent">Has been sent</p>
                  <UIToggle text="" checked={ autopay.notificationSent } onChange={ updateProp("notificationSent") } dataPoint="Add_Autopay_Has_Been_Sent_Toggle" />
              </label>
          
              {
                  ((autopay.duration === "Until a specific date" || autopay.duration === "For a specific number of payments") && !showShortForm) &&
                  <label className="flex-container flex-horizontal align-center space-between last-in-series clickable">
                      <p>Is the last one in the series</p>
                      <UIToggle text="" checked={ autopay.notificationLastOne } onChange={ updateProp("notificationLastOne") } />
                  </label>
              }
          </div>

          { hasRealTimeGoodFunds &&
              <>
                  {(biller.eBillStatus !== "Active" && billerType !== "electronic") && <div className="flex-container flex-horizontal align-center bottom-page-helper-text">
                      <span className="sub-text">{ fundsMessage1 }</span>
                  </div>}
                  {biller.eBillStatus !== "Active" && <div className="flex-container flex-horizontal align-center bottom-page-helper-text">
                      <span className="sub-text">{ fundsMessage2 }</span>
                  </div>}
                  <div className="flex-container flex-horizontal align-center bottom-page-helper-text">
                      <span className="sub-text">{ fundsMessage3 }</span>
                  </div>
              </>
          }

            { isVirtualCard && 
            <div className="email-notifications-container">
                <span className="sub-text">{ Biller.getVirtualCardDisclaimer() }</span>
            </div>
             }

          {props.children}
      </div>
    </div>
  );
}